/**
 * MicrosoftSyncAdapter - Microsoft Project/Planner synchronization adapter
 * 
 * CHANGE HISTORY:
 * - 251026_0835 [pending] danielbeer: Refactored to use Integratr for Graph API calls
 * - 251026_0800 [pending] danielbeer: Initial creation
 * 
 * Syncs Delivvr projects/tasks with Microsoft Project and Microsoft Planner.
 * Uses Integratr's MicrosoftGraphIntegration for all API calls.
 */

import { BaseSyncAdapter, type SyncAdapterCapabilities } from './BaseSyncAdapter.js';
import type { SyncChange } from '../types.js';

/**
 * Microsoft Project/Planner configuration
 */
export interface MicrosoftConfig {
  // Integratr instance (REQUIRED)
  integratr: any; // MicrosoftGraphIntegration instance
  
  // Target
  service: 'project' | 'planner';
  projectId?: string; // For MS Project
  planId?: string; // For MS Planner
  groupId?: string; // For MS Planner (Office 365 Group)
  
  // Options
  syncTasks?: boolean;
  syncProjects?: boolean;
  syncAssignments?: boolean;
  syncComments?: boolean;
  autoCreateBuckets?: boolean; // For Planner
}

/**
 * Microsoft Project/Planner sync adapter
 */
export class MicrosoftSyncAdapter extends BaseSyncAdapter {
  private config: MicrosoftConfig;
  private graph: any; // MicrosoftGraphIntegration instance

  constructor(config: MicrosoftConfig) {
    super({
      name: `microsoft-${config.service}`,
      direction: 'both',
      conflictResolution: 'last-write-wins',
      entityTypes: ['project', 'task'],
    });
    
    if (!config.integratr) {
      throw new Error('MicrosoftSyncAdapter requires Integratr MicrosoftGraphIntegration instance');
    }
    
    this.config = config;
    this.graph = config.integratr;
  }

  /**
   * Get adapter capabilities
   */
  getCapabilities(): SyncAdapterCapabilities {
    return {
      pull: true,
      push: true,
      realtime: false, // MS Graph doesn't support WebSocket
      deltaSync: true, // Delta queries available
      batchSync: true,
    };
  }

  /**
   * Pull changes from Microsoft Project/Planner (required by BaseSyncAdapter)
   */
  async pull(since: number): Promise<SyncChange[]> {
    const sinceDate = new Date(since);
    return await this.fetchChanges(sinceDate);
  }

  /**
   * Push a single change to Microsoft Project/Planner (required by BaseSyncAdapter)
   */
  async push(change: SyncChange): Promise<boolean> {
    try {
      if (this.config.service === 'planner') {
        await this.pushPlannerChange(change);
      } else {
        await this.pushProjectChange(change);
      }
      return true;
    } catch (error) {
      console.error('Microsoft push failed:', error);
      return false;
    }
  }

  /**
   * Fetch changes from Microsoft Project/Planner
   */
  private async fetchChanges(since?: Date): Promise<SyncChange[]> {
    const changes: SyncChange[] = [];

    if (this.config.service === 'planner') {
      changes.push(...await this.fetchPlannerChanges(since));
    } else {
      changes.push(...await this.fetchProjectChanges(since));
    }

    return changes;
  }

  /**
   * Fetch changes from Microsoft Planner
   */
  private async fetchPlannerChanges(since?: Date): Promise<SyncChange[]> {
    const changes: SyncChange[] = [];

    if (!this.config.planId) {
      throw new Error('Microsoft Planner: planId required');
    }

    // Fetch tasks
    const endpoint = `/planner/plans/${this.config.planId}/tasks`;
    const deltaEndpoint = since ? `${endpoint}/delta` : endpoint;
    
    const response = await this.graphRequest(deltaEndpoint);
    const tasks = response.value || [];

    for (const task of tasks) {
      changes.push({
        id: task.id,
        type: task['@removed'] ? 'delete' : (since ? 'update' : 'create'),
        entityType: 'task',
        data: this.mapPlannerTaskToDelivvr(task),
        _modified: new Date(task.lastModifiedDateTime || task.createdDateTime).getTime(),
      });
    }

    return changes;
  }

  /**
   * Fetch changes from Microsoft Project
   */
  private async fetchProjectChanges(since?: Date): Promise<SyncChange[]> {
    const changes: SyncChange[] = [];

    if (!this.config.projectId) {
      throw new Error('Microsoft Project: projectId required');
    }

    // Fetch project tasks
    const endpoint = `/projects/${this.config.projectId}/tasks`;
    const response = await this.graphRequest(endpoint);
    const tasks = response.value || [];

    for (const task of tasks) {
      const taskDate = new Date(task.lastModifiedDateTime || task.createdDateTime);
      
      if (since && taskDate <= since) {
        continue;
      }

      changes.push({
        id: task.id,
        type: 'update',
        entityType: 'task',
        data: this.mapProjectTaskToDelivvr(task),
        _modified: taskDate.getTime(),
      });
    }

    return changes;
  }

  /**
   * Push multiple changes to Microsoft Project/Planner
   */
  async pushChanges(changes: SyncChange[]): Promise<{ pushed: number; failed: number }> {
    let pushed = 0;
    let failed = 0;

    for (const change of changes) {
      try {
        const success = await this.push(change);
        if (success) {
          pushed++;
        } else {
          failed++;
        }
      } catch (error) {
        failed++;
        console.error(`Failed to push change ${change.id}:`, error);
      }
    }

    return { pushed, failed };
  }

  /**
   * Push change to Microsoft Planner
   */
  private async pushPlannerChange(change: SyncChange): Promise<void> {
    const plannerData = this.mapDelivvrToPlannerTask(change.data);

    switch (change.type) {
      case 'create':
        await this.graphRequest('/planner/tasks', {
          method: 'POST',
          body: JSON.stringify({
            ...plannerData,
            planId: this.config.planId,
          }),
        });
        break;

      case 'update':
        // Get current etag
        const current = await this.graphRequest(`/planner/tasks/${change.id}`);
        
        await this.graphRequest(`/planner/tasks/${change.id}`, {
          method: 'PATCH',
          headers: {
            'If-Match': current['@odata.etag'],
          },
          body: JSON.stringify(plannerData),
        });
        break;

      case 'delete':
        const taskToDelete = await this.graphRequest(`/planner/tasks/${change.id}`);
        
        await this.graphRequest(`/planner/tasks/${change.id}`, {
          method: 'DELETE',
          headers: {
            'If-Match': taskToDelete['@odata.etag'],
          },
        });
        break;
    }
  }

  /**
   * Push change to Microsoft Project
   */
  private async pushProjectChange(change: SyncChange): Promise<void> {
    const projectData = this.mapDelivvrToProjectTask(change.data);

    switch (change.type) {
      case 'create':
        await this.graphRequest(`/projects/${this.config.projectId}/tasks`, {
          method: 'POST',
          body: JSON.stringify(projectData),
        });
        break;

      case 'update':
        await this.graphRequest(`/projects/${this.config.projectId}/tasks/${change.id}`, {
          method: 'PATCH',
          body: JSON.stringify(projectData),
        });
        break;

      case 'delete':
        await this.graphRequest(`/projects/${this.config.projectId}/tasks/${change.id}`, {
          method: 'DELETE',
        });
        break;
    }
  }

  /**
   * Map Microsoft Planner task to Delivvr format
   */
  private mapPlannerTaskToDelivvr(task): any {
    return {
      name: task.title,
      description: task.details?.description || '',
      status: this.mapPlannerStatus(task.percentComplete),
      priority: this.mapPlannerPriority(task.priority),
      assigneeId: task.assignments ? Object.keys(task.assignments)[0] : undefined,
      dueDate: task.dueDateTime,
      startDate: task.startDateTime,
      completedDate: task.completedDateTime,
      progress: task.percentComplete / 100,
      bucketId: task.bucketId,
      metadata: {
        msPlannerId: task.id,
        msEtag: task['@odata.etag'],
        orderHint: task.orderHint,
      },
    };
  }

  /**
   * Map Microsoft Project task to Delivvr format
   */
  private mapProjectTaskToDelivvr(task): any {
    return {
      name: task.name,
      description: task.notes || '',
      status: this.mapProjectStatus(task.status),
      priority: this.mapProjectPriority(task.priority),
      assigneeId: task.assignedTo?.id,
      dueDate: task.finish,
      startDate: task.start,
      completedDate: task.actualFinish,
      progress: task.percentComplete / 100,
      estimatedHours: task.work ? task.work / 60 : undefined, // Convert minutes to hours
      actualHours: task.actualWork ? task.actualWork / 60 : undefined,
      metadata: {
        msProjectId: task.id,
        wbs: task.wbs,
        duration: task.duration,
      },
    };
  }

  /**
   * Map Delivvr task to Microsoft Planner format
   */
  private mapDelivvrToPlannerTask(task): any {
    return {
      title: task.name,
      percentComplete: Math.round((task.progress || 0) * 100),
      priority: this.mapDelivvrPriorityToPlanner(task.priority),
      dueDateTime: task.dueDate,
      startDateTime: task.startDate,
      assignments: task.assigneeId ? {
        [task.assigneeId]: {
          '@odata.type': '#microsoft.graph.plannerAssignment',
          orderHint: ' !',
        },
      } : undefined,
    };
  }

  /**
   * Map Delivvr task to Microsoft Project format
   */
  private mapDelivvrToProjectTask(task): any {
    return {
      name: task.name,
      notes: task.description || '',
      status: this.mapDelivvrStatusToProject(task.status),
      priority: this.mapDelivvrPriorityToProject(task.priority),
      start: task.startDate,
      finish: task.dueDate,
      percentComplete: Math.round((task.progress || 0) * 100),
      work: task.estimatedHours ? task.estimatedHours * 60 : undefined, // Convert hours to minutes
      actualWork: task.actualHours ? task.actualHours * 60 : undefined,
    };
  }

  /**
   * Map Planner status to Delivvr status
   */
  private mapPlannerStatus(percentComplete: number): string {
    if (percentComplete === 0) return 'todo';
    if (percentComplete === 100) return 'done';
    if (percentComplete >= 50) return 'review';
    return 'in-progress';
  }

  /**
   * Map Planner priority to Delivvr priority
   */
  private mapPlannerPriority(priority: number): string {
    // Planner: 0-10 (0=urgent, 5=medium, 10=low)
    if (priority <= 2) return 'critical';
    if (priority <= 4) return 'high';
    if (priority <= 7) return 'medium';
    return 'low';
  }

  /**
   * Map Project status to Delivvr status
   */
  private mapProjectStatus(status: string): string {
    const statusMap: Record<string, string> = {
      'NotStarted': 'todo',
      'InProgress': 'in-progress',
      'Completed': 'done',
      'Deferred': 'on-hold',
      'WaitingOnOthers': 'blocked',
    };
    return statusMap[status] || 'todo';
  }

  /**
   * Map Project priority to Delivvr priority
   */
  private mapProjectPriority(priority: number): string {
    // Project: 100-1000 (500=medium)
    if (priority >= 800) return 'critical';
    if (priority >= 600) return 'high';
    if (priority >= 400) return 'medium';
    return 'low';
  }

  /**
   * Map Delivvr priority to Planner priority
   */
  private mapDelivvrPriorityToPlanner(priority: string): number {
    const priorityMap: Record<string, number> = {
      'critical': 1,
      'high': 3,
      'medium': 5,
      'low': 9,
    };
    return priorityMap[priority] || 5;
  }

  /**
   * Map Delivvr status to Project status
   */
  private mapDelivvrStatusToProject(status: string): string {
    const statusMap: Record<string, string> = {
      'todo': 'NotStarted',
      'in-progress': 'InProgress',
      'review': 'InProgress',
      'done': 'Completed',
      'on-hold': 'Deferred',
      'blocked': 'WaitingOnOthers',
      'cancelled': 'Completed',
    };
    return statusMap[status] || 'NotStarted';
  }

  /**
   * Map Delivvr priority to Project priority
   */
  private mapDelivvrPriorityToProject(priority: string): number {
    const priorityMap: Record<string, number> = {
      'critical': 900,
      'high': 700,
      'medium': 500,
      'low': 300,
    };
    return priorityMap[priority] || 500;
  }

  /**
   * Test connection to Microsoft Graph API
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.authenticate();
      
      // Test with a simple request
      if (this.config.service === 'planner' && this.config.planId) {
        await this.graphRequest(`/planner/plans/${this.config.planId}`);
      } else if (this.config.projectId) {
        await this.graphRequest(`/projects/${this.config.projectId}`);
      }
      
      return true;
    } catch (error) {
      console.error('Microsoft connection test failed:', error);
      return false;
    }
  }
}
